import * as THREE from "three";
import { computeKinematics } from "../forwardKinematics";
import { solvePositionIk } from "../solver";
import type { IkChain, IkKinematicsState, IkResult, IkTarget } from "../types";
import { quatAngleError } from "../orientationTargets";
import { linkHeightSceneYFromFk, minLinkHeightSceneYFromFk } from "./chainHeuristics";
import type { PickPlaceStage } from "./pickPlaceServoConfig";
import { clamp, projectOntoPlane } from "./spatialUtils";

type PickPlaceIkSolveArgs = {
  chain: IkChain;
  poseTarget: IkTarget;
  posTarget: IkTarget;
  seed: number[];
  fkSeed: IkKinematicsState;
  desiredRotZ: THREE.Quaternion;
  enforceGraspOrientation: boolean;
  isNearGroundGraspPick: boolean;
  stage: PickPlaceStage;
  activeTargetPosScene: THREE.Vector3;
  zToY: THREE.Quaternion;
  dofKindByIndex: Map<number, "hinge" | "slide">;
  dofLimitByIndex: Map<number, [number, number]>;
  elbowLinkIdx: number | null;
  elbowDofIndex: number | null;
  shoulderLiftDofIndex: number | null;
  shoulderPanLinkIdx: number | null;
  shoulderPanDofIndex: number | null;
};

const wrapToPi = (x: number) => {
  const twoPi = 2 * Math.PI;
  let y = (x + Math.PI) % twoPi;
  if (y < 0) y += twoPi;
  return y - Math.PI;
};

const isFiniteLimit = (limit: [number, number] | null | undefined): limit is [number, number] =>
  !!limit && Number.isFinite(limit[0]) && Number.isFinite(limit[1]) && limit[1] > limit[0];

function solveWithSeed({
  chain,
  poseTarget,
  posTarget,
  seedVariant,
  desiredRotZ,
  enforceGraspOrientation,
}: Pick<
  PickPlaceIkSolveArgs,
  "chain" | "poseTarget" | "posTarget" | "desiredRotZ" | "enforceGraspOrientation"
> & {
  seedVariant: number[];
}): IkResult {
  const poseOptions = {
    maxIterations: 192,
    tolerance: 3e-4,
    rotationTolerance: 3e-2,
    damping: 0.02,
    stepSize: 0.6,
    orientationWeight: enforceGraspOrientation ? 1.6 : 0.8,
  };
  const posOptions = {
    maxIterations: 192,
    tolerance: 3e-4,
    damping: 0.02,
    stepSize: 0.6,
  };

  const poseAttempt = solvePositionIk(chain, poseTarget, seedVariant, poseOptions);
  if (poseAttempt.status === "success") return poseAttempt;
  if (enforceGraspOrientation) return poseAttempt;

  const posAttempt = solvePositionIk(chain, posTarget, seedVariant, posOptions);
  try {
    const fkPose = computeKinematics(chain, poseAttempt.jointValues);
    const fkPos = computeKinematics(chain, posAttempt.jointValues);
    const rotPose =
      fkPose.endEffector.rotation instanceof THREE.Quaternion
        ? quatAngleError(fkPose.endEffector.rotation, desiredRotZ)
        : Math.PI;
    const rotPos =
      fkPos.endEffector.rotation instanceof THREE.Quaternion
        ? quatAngleError(fkPos.endEffector.rotation, desiredRotZ)
        : Math.PI;

    const posePosErr = Number.isFinite(poseAttempt.error) ? Number(poseAttempt.error) : Infinity;
    const posPosErr = Number.isFinite(posAttempt.error) ? Number(posAttempt.error) : Infinity;
    const closeEnough = posePosErr <= Math.min(0.06, posPosErr + 0.03);
    const muchBetterOrient = rotPose + 0.18 < rotPos;
    return closeEnough && muchBetterOrient ? poseAttempt : posAttempt;
  } catch {
    return posAttempt;
  }
}

function scoreAttempt(
  a: IkResult | null,
  args: Pick<
    PickPlaceIkSolveArgs,
    | "chain"
    | "seed"
    | "desiredRotZ"
    | "isNearGroundGraspPick"
    | "zToY"
    | "dofKindByIndex"
    | "dofLimitByIndex"
    | "elbowLinkIdx"
    | "shoulderLiftDofIndex"
    | "shoulderPanDofIndex"
  > & { allowShoulderPanJump?: boolean }
) {
  if (!a || a.error > 0.08) return null;
  let fk: IkKinematicsState | null;
  try {
    fk = computeKinematics(args.chain, a.jointValues);
  } catch {
    fk = null;
  }
  if (!fk) return null;

  const minY = minLinkHeightSceneYFromFk({ fk, zToY: args.zToY });
  const floorY = args.isNearGroundGraspPick ? 0.01 : 0.02;
  const floorRejectSlack = args.isNearGroundGraspPick ? 0.006 : 0.002;
  if (minY < floorY - floorRejectSlack) return null;

  const errTerm = -10 * a.error;
  const rotErr =
    fk.endEffector.rotation instanceof THREE.Quaternion
      ? quatAngleError(fk.endEffector.rotation, args.desiredRotZ)
      : Math.PI;
  const rotTerm = -3.2 * rotErr;
  const elbowY =
    args.elbowLinkIdx != null
      ? linkHeightSceneYFromFk({ fk, linkIdx: args.elbowLinkIdx, zToY: args.zToY })
      : null;
  const elbowTerm = elbowY != null ? elbowY * 0.6 : 0;

  let shoulderTerm = 0;
  if (args.shoulderLiftDofIndex != null) {
    const q = a.jointValues?.[args.shoulderLiftDofIndex];
    const linkForLimit = args.chain.links.find((l) => l.dofIndex === args.shoulderLiftDofIndex);
    const limit = linkForLimit?.limit;
    if (Number.isFinite(q) && isFiniteLimit(limit)) {
      const mid = 0.5 * (limit[0] + limit[1]);
      const half = 0.5 * (limit[1] - limit[0]);
      const norm = half > 1e-6 ? (Number(q) - mid) / half : 0;
      shoulderTerm = -0.85 * norm * norm;
    }
  }

  let continuityTerm;
  try {
    let sumSq = 0;
    let count = 0;
    for (let i = 0; i < a.jointValues.length; i += 1) {
      if (
        args.allowShoulderPanJump &&
        args.shoulderPanDofIndex != null &&
        i === args.shoulderPanDofIndex
      ) {
        continue;
      }
      const q = a.jointValues[i];
      const s = args.seed[i];
      if (!Number.isFinite(q) || !Number.isFinite(s)) continue;
      const kind = args.dofKindByIndex.get(i) || "hinge";
      const limit = args.dofLimitByIndex.get(i) || null;
      const range = isFiniteLimit(limit) ? Number(limit[1]) - Number(limit[0]) : null;
      const rawDiff = kind === "hinge" ? wrapToPi(Number(q) - Number(s)) : Number(q) - Number(s);
      const normDiff = range && range > 1e-6 ? rawDiff / range : rawDiff;
      sumSq += normDiff * normDiff;
      count += 1;
    }
    continuityTerm = -1.25 * Math.sqrt(sumSq / Math.max(1, count));
  } catch {
    continuityTerm = 0;
  }

  return errTerm + rotTerm + elbowTerm + shoulderTerm + continuityTerm;
}

function shouldTryShoulderPanFlip(args: PickPlaceIkSolveArgs & { attempt: IkResult | null }) {
  const {
    chain,
    fkSeed,
    attempt,
    stage,
    activeTargetPosScene,
    zToY,
    shoulderLiftDofIndex,
    shoulderPanLinkIdx,
    shoulderPanDofIndex,
    elbowLinkIdx,
  } = args;
  if (shoulderPanDofIndex == null) return false;
  if (shoulderPanLinkIdx == null) return false;
  if (stage === "final" || stage === "close") return false;

  let largeTurn = false;
  try {
    const st = fkSeed.linkStates?.[shoulderPanLinkIdx] || null;
    const anchorZ = st?.anchorWorld instanceof THREE.Vector3 ? st.anchorWorld.clone() : null;
    const axisZ = st?.axisWorld instanceof THREE.Vector3 ? st.axisWorld.clone() : null;
    if (anchorZ && axisZ) {
      const anchorScene = anchorZ.applyQuaternion(zToY);
      const axisScene = axisZ.applyQuaternion(zToY).normalize();
      const eeScene = fkSeed.endEffector.position.clone().applyQuaternion(zToY);
      const refVec = projectOntoPlane(eeScene.clone().sub(anchorScene), axisScene);
      const tgtVec = projectOntoPlane(activeTargetPosScene.clone().sub(anchorScene), axisScene);
      if (refVec.lengthSq() > 1e-10 && tgtVec.lengthSq() > 1e-10) {
        refVec.normalize();
        tgtVec.normalize();
        const sin = axisScene.dot(refVec.clone().cross(tgtVec));
        const cos = clamp(refVec.dot(tgtVec), -1, 1);
        largeTurn = Math.abs(Math.atan2(sin, cos)) > Math.PI * 0.65;
      }
    }
  } catch {
    largeTurn = false;
  }

  let attemptLooksBad = false;
  try {
    if (!attempt || attempt.status !== "success" || attempt.error > 0.08) {
      attemptLooksBad = true;
    } else {
      const fkAttempt = computeKinematics(chain, attempt.jointValues);
      const minY = minLinkHeightSceneYFromFk({ fk: fkAttempt, zToY });
      if (!Number.isFinite(minY) || minY < 0.03) attemptLooksBad = true;

      const elbowY =
        elbowLinkIdx != null
          ? linkHeightSceneYFromFk({ fk: fkAttempt, linkIdx: elbowLinkIdx, zToY })
          : null;
      if (elbowY != null && elbowY < 0.08) attemptLooksBad = true;

      if (shoulderLiftDofIndex != null) {
        const q = attempt.jointValues?.[shoulderLiftDofIndex];
        const linkForLimit = chain.links.find((l) => l.dofIndex === shoulderLiftDofIndex);
        const limit = linkForLimit?.limit;
        if (Number.isFinite(q) && isFiniteLimit(limit)) {
          const mid = 0.5 * (limit[0] + limit[1]);
          const half = 0.5 * (limit[1] - limit[0]);
          const norm = half > 1e-6 ? (Number(q) - mid) / half : 0;
          if (Math.abs(norm) > 0.72) attemptLooksBad = true;
        }
      }
    }
  } catch {
    attemptLooksBad = true;
  }

  return largeTurn || attemptLooksBad;
}

export function solvePickPlaceIkAttempt(args: PickPlaceIkSolveArgs): IkResult {
  const { chain, seed, fkSeed, zToY, elbowLinkIdx, elbowDofIndex, shoulderLiftDofIndex, stage } =
    args;
  let attempt = solveWithSeed({ ...args, seedVariant: seed });
  const elbowSeedY =
    elbowLinkIdx != null
      ? linkHeightSceneYFromFk({ fk: fkSeed, linkIdx: elbowLinkIdx, zToY })
      : null;
  const shouldTryElbowUp =
    elbowDofIndex != null &&
    elbowSeedY != null &&
    elbowSeedY < 0.08 &&
    stage !== "final" &&
    stage !== "close";

  const shouldTryShoulderMid = (() => {
    if (shoulderLiftDofIndex == null) return false;
    const linkForLimit = chain.links.find((l) => l.dofIndex === shoulderLiftDofIndex);
    const limit = linkForLimit?.limit;
    if (!isFiniteLimit(limit)) return false;
    const q = seed?.[shoulderLiftDofIndex];
    if (!Number.isFinite(q)) return false;
    const mid = 0.5 * (limit[0] + limit[1]);
    const half = 0.5 * (limit[1] - limit[0]);
    const norm = half > 1e-6 ? (Number(q) - mid) / half : 0;
    return (shouldTryElbowUp || Math.abs(norm) > 0.45) && stage !== "final" && stage !== "close";
  })();

  const tryShoulderPanFlip = shouldTryShoulderPanFlip({ ...args, attempt });

  if (shouldTryElbowUp || shouldTryShoulderMid || tryShoulderPanFlip) {
    const candidates: Array<IkResult | null> = [attempt];

    if (shouldTryElbowUp && elbowDofIndex != null) {
      const linkForLimit = chain.links.find((l) => l.dofIndex === elbowDofIndex);
      const limit = linkForLimit?.limit;
      const base = seed[elbowDofIndex] ?? 0;
      const bump = (delta: number) => {
        const s = seed.slice();
        const proposed = base + delta;
        s[elbowDofIndex] = limit ? clamp(proposed, limit[0], limit[1]) : proposed;
        return s;
      };
      candidates.push(solveWithSeed({ ...args, seedVariant: bump(+1.05) }));
      candidates.push(solveWithSeed({ ...args, seedVariant: bump(-1.05) }));
    }

    if (shouldTryShoulderMid && shoulderLiftDofIndex != null) {
      const linkForLimit = chain.links.find((l) => l.dofIndex === shoulderLiftDofIndex);
      const limit = linkForLimit?.limit;
      if (isFiniteLimit(limit)) {
        const s = seed.slice();
        s[shoulderLiftDofIndex] = 0.5 * (limit[0] + limit[1]);
        candidates.push(solveWithSeed({ ...args, seedVariant: s }));
      }
    }

    if (tryShoulderPanFlip && args.shoulderPanDofIndex != null) {
      const linkForLimit = chain.links.find((l) => l.dofIndex === args.shoulderPanDofIndex);
      const limit = linkForLimit?.limit;
      const base = seed[args.shoulderPanDofIndex] ?? 0;
      const flip = (delta: number) => {
        const s = seed.slice();
        const proposed = base + delta;
        s[args.shoulderPanDofIndex as number] = limit
          ? clamp(proposed, limit[0], limit[1])
          : proposed;
        return s;
      };
      candidates.push(solveWithSeed({ ...args, seedVariant: flip(+Math.PI) }));
      candidates.push(solveWithSeed({ ...args, seedVariant: flip(-Math.PI) }));
    }

    let best: IkResult | null = null;
    let bestScore: number | null = null;
    for (const cand of candidates) {
      const sc = scoreAttempt(cand, { ...args, allowShoulderPanJump: tryShoulderPanFlip });
      if (sc == null) continue;
      if (bestScore == null || sc > bestScore) {
        bestScore = sc;
        best = cand;
      }
    }
    if (best) attempt = best;
  }

  return attempt;
}
